import { ArrowRight, Check, Image, Megaphone, Sparkles } from "lucide-react";

import {
  getBrandAssistantTipCreationTool,
  type BrandAssistantCreationPreviewToolName,
  type BrandAssistantTip,
} from "@runcheetah/shared";
import { Button } from "@runcheetah/ui";

export const QUICK_PROMPTS = [
  {
    labelId: "pulse.quickPrompt.createCampaign.label",
    labelDefault: "Create campaign",
    promptId: "pulse.quickPrompt.createCampaign.prompt",
    promptDefault:
      "Create a new campaign for this brand. Use what Cheetah knows about the audience, brand voice, recent posts, visual identity, and performance signals. Propose the campaign direction, post mix, schedule, hooks, captions, creative direction, and CTAs, then generate the campaign assets for review.",
    creationTool: "preview_campaign_creation" as const,
    icon: Megaphone,
  },
  {
    labelId: "pulse.quickPrompt.brainstormIdeas.label",
    labelDefault: "Brainstorm ideas",
    promptId: "pulse.quickPrompt.brainstormIdeas.prompt",
    promptDefault:
      "Brainstorm 3-5 strong campaign or post ideas for {brandTitle} right now. Use what Cheetah knows about the audience, brand voice, recent posts, and social performance. Include hooks, emotional angles, suggested formats, platform fit, CTAs, and caption direction. End with the single best idea to turn into generated assets first.",
    creationTool: null,
    icon: Sparkles,
  },
  {
    labelId: "pulse.quickPrompt.brandSummary.label",
    labelDefault: "What has Cheetah learned?",
    promptId: "pulse.quickPrompt.brandSummary.prompt",
    promptDefault:
      "Summarize what Cheetah has learned about this brand's positioning, audience, voice, and visual identity.",
    creationTool: null,
    icon: Megaphone,
  },
  {
    labelId: "pulse.quickPrompt.visualIdentity.label",
    labelDefault: "What visuals define this brand?",
    promptId: "pulse.quickPrompt.visualIdentity.prompt",
    promptDefault:
      "What visual identity cues, logo details, and design patterns has Cheetah found for this brand?",
    creationTool: null,
    icon: Image,
  },
  {
    labelId: "pulse.quickPrompt.pendingReview.label",
    labelDefault: "Show me what's pending review",
    promptId: "pulse.quickPrompt.pendingReview.prompt",
    promptDefault: "Show me what's pending review and what needs my attention first.",
    creationTool: null,
    icon: Check,
  },
] as const;

export function QuickPromptButton({
  label,
  icon: Icon,
  disabled,
  onClick,
}: {
  label: string;
  icon: typeof Sparkles;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      className="h-12 rounded-full border-border/80 bg-card/88 px-5 text-sm font-semibold shadow-sm transition hover:-translate-y-0.5 hover:bg-card"
      disabled={disabled}
      onClick={onClick}
    >
      <Icon className="h-4 w-4 text-foreground/65" aria-hidden="true" />
      {label}
      <ArrowRight className="h-4 w-4 text-foreground/38" aria-hidden="true" />
    </Button>
  );
}

export function AssistantSuggestionPills({
  title,
  tips,
  disabled,
  onPrompt,
}: {
  title?: string | null;
  tips: readonly BrandAssistantTip[];
  disabled: boolean;
  onPrompt: (prompt: string, creationTool: BrandAssistantCreationPreviewToolName | null) => void;
}) {
  if (tips.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      {title ? <div className="px-1 text-xs font-medium text-muted-foreground">{title}</div> : null}
      <div className="flex flex-wrap gap-2">
        {tips.map((tip) => (
          <Button
            key={`${tip.label}:${tip.prompt}`}
            type="button"
            variant="outline"
            className="h-8 rounded-full border-border/80 bg-background/82 px-3 text-xs font-semibold shadow-sm transition hover:-translate-y-0.5 hover:bg-background"
            disabled={disabled}
            onClick={() => {
              const submission = getAssistantSuggestionSubmission(tip);
              onPrompt(submission.prompt, submission.creationTool);
            }}
          >
            {tip.label}
            <ArrowRight className="h-3.5 w-3.5 text-foreground/38" aria-hidden="true" />
          </Button>
        ))}
      </div>
    </div>
  );
}

export function getAssistantSuggestionSubmission(tip: BrandAssistantTip): {
  prompt: string;
  creationTool: BrandAssistantCreationPreviewToolName | null;
} {
  return {
    prompt: tip.prompt,
    creationTool: getBrandAssistantTipCreationTool(tip),
  };
}
